/**
 * Portunus API Module
 * Handles authorization, abilities, tenant management, and DAC roles mapping
 */

import axiosClient, { isNoAuthMode } from './client';
import { AbilitiesResponse, TenantIdResponse, PlatformListResponse } from './types';
import { getSessionId, getAuthType } from '../security/authStorage';
import { isAuthPassEnabled } from '../security/authHelpers';

// ============================================================================
// Abilities Management
// ============================================================================

/**
 * Get user abilities for specific tenant
 */
export async function getAbilitiesForTenant(tenantId: string): Promise<AbilitiesResponse> {
  // No-auth mode loads from local file
  if (isNoAuthMode()) {
    const response = await axiosClient.get<AbilitiesResponse>('./local/abilities.json');
    return response.data;
  }
  
  const authType = getAuthType();
  const headers: Record<string, string> = { 'authtype': authType, 'modulename': tenantId };
  
  const response = await axiosClient.get<AbilitiesResponse>('./portunus/abilities', {
    headers,
  });
  
  return response.data;
}

// ============================================================================
// Tenant Management
// ============================================================================

/**
 * Get user's tenant IDs
 */
export async function getTenantIds(): Promise<string[]> {
  // No-auth mode loads from local file
  if (isNoAuthMode()) {
    const response = await axiosClient.get('./local/tenantIds.json');
    return response.data.roles || [];
  }
  
  const authType = getAuthType();
  
  const response = await axiosClient.get<TenantIdResponse>('./portunus/tenantId', {
    headers: {
      'authtype': authType,
    },
  });
  
  return response.data.roles || [];
}

// ============================================================================
// Platform List
// ============================================================================

/**
 * Get the list of available platforms and their instances for the given OHID account.
 *
 * Platforms are only ever scoped to an OHID account (the concept doesn't exist for
 * MSID), so `selectedAccountId` is required and always sent as the `selected-account-id` header.
 */
export async function getPlatformList(selectedAccountId: string): Promise<PlatformListResponse> {
  // No-auth mode loads from local file
  if (isNoAuthMode()) {
    const response = await axiosClient.get<PlatformListResponse>('./local/platformList.json');
    return response.data;
  }

  const headers: Record<string, string> = {
    'authtype': 'ohid',
    'selected-account-id': selectedAccountId,
  };

  const response = await axiosClient.get<PlatformListResponse>('./portunus/platform-list', { headers });

  return response.data;
}

export async function setSelectedPlatform(platform: string): Promise<void> {
  if (isNoAuthMode()) {
    return;
  }

  await axiosClient.post('./portunus/selected-platform', { platform });
}

// ============================================================================
// DAC Roles Mapping
// ============================================================================

/**
 * Create/update DAC roles mapping for the given tenant IDs
 *
 * Best-effort, fire-and-forget call — this never rejects. Failures are
 * already logged by the axios response interceptor, and callers don't need
 * DAC roles mapping to succeed in order to proceed, so errors are swallowed
 * here and `undefined` is returned instead of propagating a rejection.
 *
 * No-op in no-auth mode — DAC roles mapping is not needed locally. When
 * `authpassEnabled` is true, the `modulename` header (tenantId) is included
 * so the auth-proxy can scope the roles mapping to that tenant. Otherwise,
 * the header is omitted for the legacy flow.
 */
export async function createDGRoles(tenantIds: any, tenantId?: any) {
  if (isNoAuthMode()) {
    return;
  }

  const headers: Record<string, string> = {
    'session-id': getSessionId() as string,
    'authtype': getAuthType() as string,
  };

  if (isAuthPassEnabled()) {
    headers['modulename'] = tenantId;
  }

  try {
    return await axiosClient.post(`/portunus/dac/roles/mapping`, tenantIds, { headers });
  } catch {
    return undefined;
  }
}
